import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  Configuration,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication
} from '@azure/msal-browser';
import { LoggerService } from '@core/services/logger.service';
import { environment } from '@environments/environment';

export const msalClientFactory = (logger: LoggerService): IPublicClientApplication => {
  // Every MSAL-internal diagnostic (token acquisition failures, silent-refresh failures,
  // interaction_required errors) was previously generated and immediately thrown away by an empty
  // callback — the single largest authentication observability gap on the frontend. Errors/Warnings are
  // routed through in all environments; Info only outside production, to avoid dominating telemetry
  // volume for no diagnostic gain. The configured logLevel below caps MSAL at Info — Verbose/Trace are
  // not requested at all, since MSAL fires those on effectively every cache read/write and internal state
  // transition (a firehose, not "routine success"); asking for them here would flood Application Insights
  // with a trackEvent call per message regardless of any filtering done in this callback.
  const loggerCallback = (logLevel: LogLevel, message: string) => {
    switch (logLevel) {
      case LogLevel.Error:
        logger.error(`MSAL: ${message}`);
        break;
      case LogLevel.Warning:
        logger.warn(`MSAL: ${message}`);
        break;
      default:
        if (!environment.production) {
          logger.info(`MSAL: ${message}`);
        }
        break;
    }
  };

  const configuration = {
    auth: {
      clientId: environment.authenticationAudience,
      authority: environment.authenticationAuthority,
      redirectUri: environment.redirectUri,
      postLogoutRedirectUri: '/'
    },

    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        // A latent risk with an empty sink: harmless while nothing reads the logs, but would leak
        // UPNs/names into telemetry now that loggerCallback actually forwards messages.
        piiLoggingEnabled: false
      }
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    }
  } as Configuration;

  return new PublicClientApplication(configuration);
};

export const msalGuardConfigFactory = (): MsalGuardConfiguration => ({
  interactionType: InteractionType.Redirect,
  authRequest: {
    scopes: [environment.authorizationScope]
  }
});

export const msalInterceptorConfigFactory = (): MsalInterceptorConfiguration => ({
  interactionType: InteractionType.Redirect,
  protectedResourceMap: new Map([[environment.apiBaseUrl + '*', [environment.authorizationScope]]])
});
